/**
 * Tab component is originally taken from
 * https://clubmate.fi/tabs#result-tabpanel-1
 */

import React, { FC, useRef } from 'react';
import { ChevronLeft } from 'react-feather';
import styled from 'styled-components';
import { useEditorData } from '../../lib/FabricJs';
import { editorTheme, TOOLBAR_HEIGHT } from '../../theme';

const TAB_PANEL_WIDTH = 320;
const RAIL_WIDTH = 72;

interface StyleProps {
  isActive: boolean;
}

interface TabProps {
  tabs: Array<{
    title: string;
    panelTitle?: string;
    icon?: JSX.Element;
    children?: JSX.Element;
  }>;
  id: string;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 0;
`;

const TabList = styled.ul`
  display: flex;
  list-style-type: none;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: ${RAIL_WIDTH}px;
  padding: 8px 0 0;
  margin: 0;
  background-color: ${editorTheme.rail};
`;

const TabItem = styled.li`
  margin: 0;
`;

const Tab = styled.a<StyleProps>`
  width: 60px;
  height: 58px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 10.5px;
  text-decoration: none;
  color: ${({ isActive }) => (isActive ? 'white' : editorTheme.railText)};
  background-color: ${({ isActive }) =>
    isActive ? editorTheme.railActive : 'transparent'};
  &:hover {
    color: white;
  }
`;

const TabPanel = styled.div<StyleProps>`
  display: ${({ isActive }) => (isActive ? 'flex' : 'none')};
  flex-direction: column;
  width: ${TAB_PANEL_WIDTH}px;
  min-height: 0;
  background: ${editorTheme.surface};
  border-right: 1px solid ${editorTheme.border};
`;

const TabPanelHeader = styled.div`
  height: ${TOOLBAR_HEIGHT}px;
  flex: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px 0 16px;
  border-bottom: 1px solid ${editorTheme.border};
  font-size: 14px;
  font-weight: 600;
  color: ${editorTheme.text};
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  color: ${editorTheme.textMuted};
  &:hover {
    background: ${editorTheme.surfaceMuted};
    color: ${editorTheme.text};
  }
`;

const TabPanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
`;

const Tabs: FC<TabProps> = ({ tabs = [], id }) => {
  const { activeTab, setActiveTab } = useEditorData();

  const tabRefs = useRef<any>({});
  const handleClick = (
    tab: string,
    event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event?.preventDefault();
    setActiveTab(activeTab === tab ? '' : tab);
  };
  const getId = (...rest: string[]) => [...rest, id].join('-');

  const focusTab = (tab: string) => {
    setActiveTab(tab);
    // Get the reference from the `tabRefs` and actual move focus to the tab
    tabRefs.current[tab].focus();
  };

  const handleKeyboard = (
    tab: string,
    event?: React.KeyboardEvent<HTMLAnchorElement>,
  ) => {
    const tabCount = tabs.length - 1;
    const currentTabIndex = tabs.findIndex((t) => t.title === tab);
    const [firstTab] = tabs;
    const lastTab = tabs[tabCount];
    const nextTab = tabs[currentTabIndex + 1];
    const prevTab = tabs[currentTabIndex - 1];
    if (event) {
      if (event.key === 'ArrowDown') {
        if (tabCount > currentTabIndex) return focusTab(nextTab.title);
        return focusTab(firstTab.title);
      }

      if (event.key === 'ArrowUp') {
        if (currentTabIndex > 0) return focusTab(prevTab.title);
        return focusTab(lastTab.title);
      }

      if (event.key === 'Home') {
        event.preventDefault();
        return focusTab(firstTab.title);
      }

      if (event.key === 'End') {
        event.preventDefault();
        return focusTab(lastTab.title);
      }
    }
  };

  return (
    <Wrap>
      <TabList role="tablist" aria-label="Insert" aria-orientation="vertical">
        {tabs.map(({ title, icon }) => {
          const isActive = activeTab === title;
          return (
            <TabItem key={title}>
              <Tab
                aria-selected={isActive}
                aria-controls={getId(title, 'tabpanel')}
                href={'#' + getId(title, 'tabpanel')}
                id={getId(title)}
                isActive={isActive}
                onClick={(e) => handleClick(title, e)}
                onKeyDown={(e) => handleKeyboard(title, e)}
                tabIndex={
                  isActive || (!activeTab && title === tabs[0].title) ? 0 : -1
                }
                ref={(ref) => (tabRefs.current[title] = ref)}
                role="tab"
              >
                {icon}
                {title}
              </Tab>
            </TabItem>
          );
        })}
      </TabList>
      {activeTab &&
        tabs.map(({ title, panelTitle, children }) => (
          <TabPanel
            aria-labelledby={getId(title)}
            id={getId(title, 'tabpanel')}
            isActive={activeTab === title}
            key={title}
            role="tabpanel"
          >
            <TabPanelHeader>
              {panelTitle || title}
              <CloseButton
                onClick={() => {
                  setActiveTab('');
                }}
                aria-label="Close panel"
              >
                <ChevronLeft size={18} />
              </CloseButton>
            </TabPanelHeader>
            {activeTab === title && (
              <TabPanelBody>{children || 'No content yet'}</TabPanelBody>
            )}
          </TabPanel>
        ))}
    </Wrap>
  );
};

export default Tabs;
